import React, { useState, useEffect } from 'react';
import Sidebar from '../components/SideBar';
import Header from '../components/Header';
import {
  FormationStats,
  FormationFilters,
  FormationList,
  FormationDetail
} from '../components/formation';
import useGlobale from '../components/global';
import useMenuItems from '../components/Menu';
import api from '../components/api';
import { toast } from 'react-toastify';
import { RefreshCw } from 'lucide-react';
import Swal from 'sweetalert2';

const FormationPage = () => {
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const { user } = useGlobale();
  const { menuItems } = useMenuItems();
  
  // États pour les données
  const [formations, setFormations] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(false);
  const [statsLoading, setStatsLoading] = useState(false);
  const [selectedFormation, setSelectedFormation] = useState(null);
  
  // États pour les filtres
  const [filters, setFilters] = useState({
    search: '',
    is_paid: '',
    auteur: '',
    ordering: '-created_at'
  });
  
  useEffect(() => {
    const handleResize = () => {
      setIsSidebarOpen(window.innerWidth >= 1024);
    };
    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Charger les statistiques
  useEffect(() => {
    fetchStats();
  }, []);

  // Charger les formations quand les filtres changent
  useEffect(() => {
    fetchFormations();
  }, [filters]);

  const fetchStats = async () => {
    try {
      setStatsLoading(true);
      const response = await api.get('/api/formations/admin/formations/stats/');
      setStats(response.data);
    } catch (error) {
      console.error('Erreur lors du chargement des statistiques:', error);
      toast.error('Erreur lors du chargement des statistiques');
    } finally {
      setStatsLoading(false);
    }
  };

  const fetchFormations = async () => {
    try {
      setLoading(true);
      
      // Construire les paramètres de requête
      const params = new URLSearchParams();
      if (filters.search) params.append('search', filters.search);
      if (filters.is_paid) params.append('is_paid', filters.is_paid);
      if (filters.auteur) params.append('auteur', filters.auteur);
      if (filters.ordering) params.append('ordering', filters.ordering);
      
      const response = await api.get(`/api/formations/admin/formations/?${params.toString()}`);
      setFormations(response.data || []);
    } catch (error) {
      console.error('Erreur lors du chargement des formations:', error);
      toast.error('Erreur lors du chargement des formations');
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({
      ...prev,
      [key]: value
    }));
  };

  const handleResetFilters = () => {
    setFilters({
      search: '',
      is_paid: '',
      auteur: '',
      ordering: '-created_at'
    });
  };

  const handleViewFormation = (formation) => {
    setSelectedFormation(formation);
  };

  const handleEditFormation = (formation) => {
    setSelectedFormation(formation);
  };

  const handleDeleteFormation = async (formation) => {
    const result = await Swal.fire({
      title: 'Supprimer cette formation ?',
      text: "Cette action est irréversible ! Tous les participants et ressources seront également supprimés.",
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#FF6B00',
      cancelButtonColor: '#6B7280',
      confirmButtonText: 'Oui, supprimer',
      cancelButtonText: 'Annuler',
      customClass: {
        popup: 'rounded-xl',
        confirmButton: 'rounded-lg px-6 py-2',
        cancelButton: 'rounded-lg px-6 py-2'
      }
    });

    if (result.isConfirmed) {
      try {
        await api.delete(`/api/formations/admin/formations/${formation.id}/`);
        Swal.fire({
          title: 'Supprimée !',
          text: 'La formation a été supprimée avec succès.',
          icon: 'success',
          confirmButtonColor: '#FF6B00',
          confirmButtonText: 'OK',
          timer: 2000,
          customClass: {
            popup: 'rounded-xl',
            confirmButton: 'rounded-lg px-6 py-2'
          }
        });
        fetchFormations();
        fetchStats();
      } catch (error) {
        console.error('Erreur lors de la suppression:', error);
        Swal.fire({
          title: 'Erreur !',
          text: 'Erreur lors de la suppression de la formation.',
          icon: 'error',
          confirmButtonColor: '#FF6B00',
          confirmButtonText: 'OK',
          customClass: {
            popup: 'rounded-xl',
            confirmButton: 'rounded-lg px-6 py-2'
          }
        });
      }
    }
  };

  const handleUpdateFormation = (updatedFormation) => {
    // Mettre à jour la liste
    setFormations(prev =>
      prev.map(f => f.id === updatedFormation.id ? updatedFormation : f)
    );
    fetchStats();
  };

  const handleRefresh = () => {
    fetchFormations();
    fetchStats();
    toast.info('Données actualisées');
  };

  const getActiveFiltersCount = () => {
    let count = 0;
    if (filters.search) count++;
    if (filters.is_paid) count++;
    if (filters.auteur) count++;
    return count;
  };

  return (
    <div className="flex min-h-screen bg-[#F4F8E9]">
      <Sidebar
        isOpen={isSidebarOpen}
        menuItems={menuItems}
        onClose={() => setIsSidebarOpen(false)}
      />

      <div className="flex-1 flex flex-col w-full">
        <Header
          isSidebarOpen={isSidebarOpen}
          setIsSidebarOpen={setIsSidebarOpen}
          user={user}
        />

        <main className="flex-1 w-full p-4 lg:p-6 overflow-auto">
          {/* Header avec titre et boutons */}
          <div className="bg-white rounded-lg shadow-sm p-4 mb-6 flex items-center justify-between">
            <div>
              <h1 className="text-2xl lg:text-3xl font-bold text-gray-900">
                Gestion des Formations
              </h1>
              <p className="text-gray-600 text-sm mt-1">
                Gérez toutes les formations de la plateforme
              </p>
            </div>
            <button
              onClick={handleRefresh}
              disabled={loading}
              className="px-4 py-2 bg-[#FF6B00] text-white rounded-lg hover:bg-[#e55d00] transition-colors flex items-center gap-2 font-medium disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <RefreshCw size={18} className={loading ? 'animate-spin' : ''} />
              <span className="hidden sm:inline">Actualiser</span>
            </button>
          </div>

          {/* Statistiques */}
          {!statsLoading && stats && <FormationStats stats={stats} />}

          {/* Filtres */}
          <FormationFilters
            filters={filters}
            onFilterChange={handleFilterChange}
            onReset={handleResetFilters}
            activeFiltersCount={getActiveFiltersCount()}
          />

          {/* Résultats count */}
          {!loading && formations.length > 0 && (
            <div className="mb-4">
              <div className="text-sm text-gray-600">
                <span className="font-medium text-gray-900">{formations.length}</span> formation{formations.length > 1 ? 's' : ''} trouvée{formations.length > 1 ? 's' : ''}
              </div>
            </div>
          )}

          {/* Liste des formations */}
          <FormationList
            formations={formations}
            loading={loading}
            onView={handleViewFormation}
            onEdit={handleEditFormation}
            onDelete={handleDeleteFormation}
          />

          {/* Modal détail */}
          {selectedFormation && (
            <FormationDetail
              formation={selectedFormation}
              onClose={() => setSelectedFormation(null)}
              onUpdate={handleUpdateFormation}
            />
          )}
        </main>
      </div>
    </div>
  );
};

export default FormationPage;